import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import RequisitionsList from "./RequisitionsList";
import CandidatesList from "./CandidatesList";
import InterviewsList from "./InterviewsList";
import OffersList from "./OffersList";
import {
  Settings,
  Plus,
  Briefcase,
  UserPlus,
  CalendarDays,
  TrendingUp,
  CheckCircle,
  Star,
  Phone,
  Video,
  ClipboardCheck,
  FileCheck,
  FileText,
  Calendar,
  Eye,
  Loader2,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import AccessRestricted from "../../components/AccessRestricted";
import api from "../../utils/api";
import { formatTimestamp, formatSydneyDateShort } from "../../utils/helpers";

interface RecruitmentStats {
  activeRequisitions: number;
  totalCandidates: number;
  upcomingInterviews: number;
  offersExtended: number;
  phoneScreen: number;
  preEmploymentCommenced: number;
  preEmploymentCompleted: number;
  offerAccepted: number;
}

interface RecentRequisition {
  id: number;
  jobRole: { roleName: string } | null;
  department: { departmentName: string } | null;
  candidateCount: number;
  status: string;
  createdBy?: { id: number; name: string } | null;
  requisitionViewers?: { id: number; user: { id: number; name: string } }[];
}

interface UpcomingInterview {
  id: number;
  interviewType: "PHONE" | "VIDEO" | "IN_PERSON";
  scheduledAt: string;
  durationMinutes: number;
  candidate: { id: number; firstName: string; lastName: string };
}

const RecruitmentPage: React.FC = () => {
  const ability = useAbility(AbilityContext);
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats] = useState<RecruitmentStats | null>(null);
  const [recentRequisitions, setRecentRequisitions] = useState<RecentRequisition[]>([]);
  const [upcomingInterviews, setUpcomingInterviews] = useState<UpcomingInterview[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingLists, setLoadingLists] = useState(true);

  const canReadList = ability.can(ACTIONS.READ, RESOURCES.RECRUITMENT_REQUEST_LIST);
  const isViewerOnly = !canReadList && ability.can(ACTIONS.READ, RESOURCES.RECRUITMENT_MODULE);

  const mainRequisitions = recentRequisitions.filter((req) =>
    String(req.createdBy?.id) === String(currentUser?.id) || !req.requisitionViewers?.some((rv) => String(rv.user.id) === currentUser?.id)
  );
  const sharedRequisitions = recentRequisitions.filter((req) =>
    req.requisitionViewers?.some((rv) => String(rv.user.id) === currentUser?.id)
  );

  const canViewDashboard = ability.can(ACTIONS.READ, RESOURCES.RECRUITMENT_DASHBOARD);

  useEffect(() => {
    if (!canViewDashboard) {
      setLoading(false);
      return;
    }
    const fetchStats = async () => {
      try {
        const response = await api.get<{ success: boolean; stats: RecruitmentStats }>(
          "/api/requisitions/stats"
        );
        if (response.success) {
          setStats(response.stats);
        }
      } catch (error) {
        console.error("Failed to fetch recruitment stats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [canViewDashboard]);

  useEffect(() => {
    const fetchLists = async () => {
      setLoadingLists(true);
      try {
        const today = new Date().toISOString();
        const [reqRes, intRes] = await Promise.all([
          api.get<{ success: boolean; requisitions: RecentRequisition[]; pagination: { total: number } }>(
            "/api/requisitions?limit=5"
          ),
          api.get<{ success: boolean; interviews: UpcomingInterview[] }>(
            `/api/interviews?status=SCHEDULED&fromDate=${encodeURIComponent(today)}&limit=5`
          ),
        ]);
        if (reqRes.success) setRecentRequisitions(reqRes.requisitions);
        if (intRes.success) setUpcomingInterviews(intRes.interviews);
      } catch (error) {
        console.error("Failed to fetch dashboard lists:", error);
      } finally {
        setLoadingLists(false);
      }
    };

    fetchLists();
  }, []);

  if (!ability.can(ACTIONS.READ, RESOURCES.RECRUITMENT_MODULE)) {
    return <AccessRestricted />;
  }

  const statCards = [
    {
      label: "Active Requisitions",
      key: "activeRequisitions" as keyof RecruitmentStats,
      icon: <Briefcase size={24} />,
      iconColor: "text-blue-500",
      iconBg: "bg-blue-50",
      hoverBg: "group-hover:bg-blue-50",
      hoverIcon: "group-hover:text-blue-500",
    },
    {
      label: "Total Candidates",
      key: "totalCandidates" as keyof RecruitmentStats,
      icon: <UserPlus size={24} />,
      iconColor: "text-green-500",
      iconBg: "bg-green-50",
      hoverBg: "group-hover:bg-green-50",
      hoverIcon: "group-hover:text-green-500",
    },
    {
      label: "Phone Screen",
      key: "phoneScreen" as keyof RecruitmentStats,
      icon: <Phone size={24} />,
      iconColor: "text-indigo-500",
      iconBg: "bg-indigo-50",
      hoverBg: "group-hover:bg-indigo-50",
      hoverIcon: "group-hover:text-indigo-500",
    },
    {
      label: "Upcoming Interviews",
      key: "upcomingInterviews" as keyof RecruitmentStats,
      icon: <CalendarDays size={24} />,
      iconColor: "text-purple-500",
      iconBg: "bg-purple-50",
      hoverBg: "group-hover:bg-purple-50",
      hoverIcon: "group-hover:text-purple-500",
    },
    {
      label: "Pre-employment commenced",
      key: "preEmploymentCommenced" as keyof RecruitmentStats,
      icon: <ClipboardCheck size={24} />,
      iconColor: "text-teal-500",
      iconBg: "bg-teal-50",
      hoverBg: "group-hover:bg-teal-50",
      hoverIcon: "group-hover:text-teal-500",
    },
    {
      label: "Pre-employment completed",
      key: "preEmploymentCompleted" as keyof RecruitmentStats,
      icon: <FileCheck size={24} />,
      iconColor: "text-emerald-500",
      iconBg: "bg-emerald-50",
      hoverBg: "group-hover:bg-emerald-50",
      hoverIcon: "group-hover:text-emerald-500",
    },
    {
      label: "Offer Extended",
      key: "offersExtended" as keyof RecruitmentStats,
      icon: <TrendingUp size={24} />,
      iconColor: "text-orange-500",
      iconBg: "bg-orange-50",
      hoverBg: "group-hover:bg-orange-50",
      hoverIcon: "group-hover:text-orange-500",
    },
    {
      label: "Offer Accepted",
      key: "offerAccepted" as keyof RecruitmentStats,
      icon: <CheckCircle size={24} />,
      iconColor: "text-cyan-500",
      iconBg: "bg-cyan-50",
      hoverBg: "group-hover:bg-cyan-50",
      hoverIcon: "group-hover:text-cyan-500",
    },
  ];

  const quickActions = [
    {
      label: "New Requisition",
      icon: <Plus size={20} />,
      onClick: () => navigate("/recruitment/new-requisition"),
    },
    {
      label: "Interview Templates",
      icon: <FileText size={20} />,
      onClick: () => navigate("/recruitment/interview-templates"),
    },
    {
      label: "Values Management",
      icon: <Star size={20} />,
      onClick: () => navigate("/recruitment/values"),
    },
    {
      label: "Upcoming Interviews",
      icon: <Calendar size={20} />,
      onClick: () => setActiveTab("interviews"),
    },
  ];

  const getRequisitionStatusBadgeStyle = (status: string) => {
    switch (status) {
      case "OPEN":
        return "bg-yellow-50 text-yellow-700 border-yellow-100";
      case "IN_PROGRESS":
        return "bg-blue-50 text-blue-700 border-blue-100";
      case "ON_HOLD":
        return "bg-orange-50 text-orange-700 border-orange-100";
      case "COMPLETE":
        return "bg-green-50 text-green-700 border-green-100";
      case "CANCELLED":
        return "bg-red-50 text-red-700 border-red-100";
      default:
        return "bg-gray-50 text-gray-600 border-gray-100";
    }
  };

  const getInterviewTypeIcon = (type: string) => {
    switch (type) {
      case "PHONE": return <Phone size={16} className="text-indigo-500" />;
      case "VIDEO": return <Video size={16} className="text-purple-500" />;
      case "IN_PERSON": return <Briefcase size={16} className="text-blue-500" />;
      default: return <CalendarDays size={16} className="text-gray-400" />;
    }
  };

  return (
    <div className="flex flex-col space-y-6 animate-in fade-in duration-500 pb-10 px-0 sm:px-4 lg:px-0">
      {/* Main Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Recruitment</h1>
          <p className="text-gray-500 text-sm font-medium">
            Manage recruitment requisitions, candidates, and interviews
          </p>
        </div>
        {/* Desktop Actions */}
        {!isViewerOnly && (
          <div className="hidden md:flex items-center gap-3">
            <Button
              variant="outline"
              className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              onClick={() => navigate("/recruitment/settings")}
            >
              <Settings size={18} />
              <span>Settings</span>
            </Button>
            {ability.can(ACTIONS.CREATE, RESOURCES.RECRUITMENT_REQUEST) && (
              <Button
                className="bg-slate-950 hover:bg-slate-900 text-white flex items-center gap-2 px-4 whitespace-nowrap"
                onClick={() => navigate("/recruitment/new-requisition")}
              >
                <Plus size={18} />
                <span>New Requisition</span>
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <div
            key={stat.label}
            className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all group overflow-hidden relative"
          >
            <div className="flex justify-between items-start">
              <div className="z-10">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">
                  {stat.label}
                </p>
                <h3 className="text-2xl font-black text-gray-900 group-hover:scale-110 transition-transform origin-left">
                  {loading ? (
                    <span className="inline-block w-8 h-6 bg-gray-200 rounded animate-pulse" />
                  ) : stats ? (
                    stats[stat.key] ?? 0
                  ) : (
                    0
                  )}
                </h3>
              </div>
              <div
                className={`p-2.5 rounded-xl transition-colors z-10 ${stat.iconBg} ${stat.hoverBg}`}
              >
                <div
                  className={`${stat.iconColor} ${stat.hoverIcon} transition-colors`}
                >
                  {stat.icon}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-gray-100 p-1 rounded-full w-fit hidden md:flex h-auto mb-6">
          <TabsTrigger
            value="overview"
            className="rounded-full px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-500 font-bold transition-all"
          >
            Overview
          </TabsTrigger>
          <TabsTrigger
            value="requisitions"
            className="rounded-full px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-500 font-bold transition-all"
          >
            Requisitions
          </TabsTrigger>
          <TabsTrigger
            value="candidates"
            className="rounded-full px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-500 font-bold transition-all"
          >
            Candidates
          </TabsTrigger>
          <TabsTrigger
            value="interviews"
            className="rounded-full px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-500 font-bold transition-all"
          >
            Interviews
          </TabsTrigger>
          <TabsTrigger
            value="offers"
            className="rounded-full px-6 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-500 font-bold transition-all"
          >
            Offers
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-0 space-y-6">
          {!isViewerOnly && (
            <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-4">
              <div>
                <h4 className="font-bold text-gray-900 text-base">Quick Actions</h4>
                <p className="text-gray-500 text-sm font-medium">Common recruitment tasks</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {quickActions.map((action) => (
                  <button
                    key={action.label}
                    onClick={action.onClick}
                    className="flex flex-col items-center justify-center gap-2 bg-white rounded-xl border border-gray-200 p-5 hover:border-gray-300 hover:shadow-sm transition-all cursor-pointer"
                  >
                    <span className="text-gray-700">{action.icon}</span>
                    <span className="text-sm font-medium text-gray-900">{action.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Recent Requisitions & Upcoming Interviews */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Requisitions */}
            <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-4">
              <div>
                <h4 className="font-bold text-gray-900 text-base">Recent Requisitions</h4>
                <p className="text-gray-500 text-sm font-medium">Latest recruitment activity</p>
              </div>

              <div className="space-y-3">
                {loadingLists ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 size={20} className="animate-spin text-gray-400" />
                  </div>
                ) : mainRequisitions.length === 0 ? (
                  <div className="py-8 text-center text-gray-400">
                    <p className="text-sm font-medium">No requisitions yet</p>
                  </div>
                ) : (
                  mainRequisitions.map((req) => (
                    <div
                      key={req.id}
                      className="flex items-center justify-between p-4 rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {req.jobRole?.roleName || "Untitled Requisition"}
                        </p>
                        <p className="text-xs text-gray-500 font-medium mt-0.5">
                          {req.department?.departmentName || "—"} · {req.candidateCount ?? 0} candidate{(req.candidateCount ?? 0) !== 1 ? "s" : ""}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 shrink-0 ml-4">
                        <Badge
                          variant="outline"
                          className={`rounded-lg border-0 font-bold px-2.5 py-1 text-[10px] ${getRequisitionStatusBadgeStyle(req.status)}`}
                        >
                          {req.status === "IN_PROGRESS" ? "In Progress" : req.status.charAt(0) + req.status.slice(1).toLowerCase()}
                        </Badge>
                        <button
                          onClick={() => navigate(`/recruitment/requisitions/${req.id}`)}
                          className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <Eye size={16} />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Upcoming Interviews */}
            <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-4">
              <div>
                <h4 className="font-bold text-gray-900 text-base">Upcoming Interviews</h4>
                <p className="text-gray-500 text-sm font-medium">Scheduled interviews this week</p>
              </div>

              <div className="space-y-3">
                {loadingLists ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 size={20} className="animate-spin text-gray-400" />
                  </div>
                ) : upcomingInterviews.length === 0 ? (
                  <div className="py-12 text-center text-gray-400">
                    <p className="text-sm font-medium">No upcoming interviews</p>
                  </div>
                ) : (
                  upcomingInterviews.map((int) => (
                    <div
                      key={int.id}
                      className="flex items-center justify-between p-4 rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors"
                    >
                      <div className="min-w-0 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center shrink-0">
                          {getInterviewTypeIcon(int.interviewType)}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900 truncate">
                            {int.candidate?.firstName} {int.candidate?.lastName}
                          </p>
                          <p className="text-xs text-gray-500 font-medium mt-0.5">
                            {formatTimestamp(int.scheduledAt)} · {int.durationMinutes} min
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => navigate(`/recruitment/candidates/${int.candidate?.id}`)}
                        className="text-gray-400 hover:text-gray-600 transition-colors shrink-0 ml-3"
                      >
                        <Eye size={16} />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Shared with Me */}
          {sharedRequisitions.length > 0 && (
            <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-4">
              <div>
                <h4 className="font-bold text-gray-900 text-base">Shared with Me</h4>
                <p className="text-gray-500 text-sm font-medium">Requisitions you were invited to view</p>
              </div>
              <div className="space-y-3">
                {sharedRequisitions.map((req) => (
                  <div
                    key={req.id}
                    className="flex items-center justify-between p-4 rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {req.jobRole?.roleName || "Untitled Requisition"}
                        </p>
                        <Badge variant="outline" className="rounded-lg border-0 bg-purple-50 text-purple-700 font-bold px-2 py-0.5 text-[10px]">
                          Shared
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-500 font-medium mt-0.5">
                        {req.department?.departmentName || "—"} · {req.candidateCount ?? 0} candidate{(req.candidateCount ?? 0) !== 1 ? "s" : ""}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0 ml-4">
                      <Badge
                        variant="outline"
                        className={`rounded-lg border-0 font-bold px-2.5 py-1 text-[10px] ${getRequisitionStatusBadgeStyle(req.status)}`}
                      >
                        {req.status === "IN_PROGRESS" ? "In Progress" : req.status.charAt(0) + req.status.slice(1).toLowerCase()}
                      </Badge>
                      <button
                        onClick={() => navigate(`/recruitment/requisitions/${req.id}`)}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <Eye size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty state for viewer-only users */}
          {isViewerOnly && mainRequisitions.length === 0 && sharedRequisitions.length === 0 && !loadingLists && (
            <div className="bg-white p-12 rounded-3xl border border-gray-100 shadow-sm text-center">
              <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-4">
                <Briefcase size={28} className="text-gray-300" />
              </div>
              <h4 className="font-bold text-gray-900 text-lg mb-1">
                You have not been invited to any Requisitions
              </h4>
            </div>
          )}
        </TabsContent>

        <TabsContent value="requisitions" className="mt-0">
          <RequisitionsList />
        </TabsContent>

        <TabsContent value="candidates" className="mt-0">
          <CandidatesList />
        </TabsContent>

        <TabsContent value="interviews" className="mt-0">
          <InterviewsList />
        </TabsContent>

        <TabsContent value="offers" className="mt-0">
          <OffersList />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default RecruitmentPage;
